import { useCallback, useMemo, useState } from "react";
import { DashboardLayout } from "@/pages/Dashboard";
import TableWithSearch from "@/components/TableWithSearch";
import { useOrg } from "@/contexts/useOrg";
import { useAuth } from "@/contexts/use-auth";
import { useGetData } from "@/hooks/useGetData";
import {
  formatDate,
  getReportDateRange,
  type ReportDatePreset,
} from "@/utils/helpers";
import {
  CONFLICTS_API_URL,
  INVALID_CONFLICTS_API_URL,
  conflictTableHeaders,
} from "../constants";
import type { ConflictRow } from "../types";
import { extractInvalidInteractions, extractUnassignedInteractions } from "../api";
import { isGraceBotCall } from "../utils";
import { ConflictCallTypeBadge } from "../components/ConflictCallTypeBadge";
import ResolveConflictDialog from "../components/ResolveConflictDialog";
import TranscriptDialog from "../components/TranscriptDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { MessageSquareText, Phone, PhoneOff, Play } from "lucide-react";
import moment from "moment";

const DATE_PRESET_OPTIONS: { value: ReportDatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 Days" },
  { value: "last15", label: "Last 15 Days" },
  { value: "last30", label: "Last 30 Days" },
  { value: "last90", label: "Last 90 Days" },
];

type ConflictsTab = "conflicts" | "invalid";

function orgDisplayName(row: ConflictRow) {
  if (row.organizationName) return row.organizationName;
  const org = row.organizationId;
  if (org && typeof org === "object") return org.organizationName || "—";
  return "—";
}

function splitCallDateTime(row: ConflictRow) {
  const raw = row.callAt || row.createdAt;
  if (!raw) return { date: "—", time: "—" };
  const m = moment(raw);
  if (!m.isValid()) return { date: "—", time: "—" };
  return {
    date: formatDate(raw),
    time: m.format("hh:mm A"),
  };
}

function transcriptPreview(row: ConflictRow) {
  const utterances = row.transcriptionUtterances ?? [];
  if (utterances.length > 0) {
    return utterances
      .map((u) => String(u.text ?? "").trim())
      .filter(Boolean)
      .join(" ");
  }
  return String(row.transcript ?? "").trim();
}

function buildConflictViewRows(
  rows: ConflictRow[],
  onOpenTranscript: (row: ConflictRow) => void,
) {
  return rows.map((row) => {
    const { date, time } = splitCallDateTime(row);
    const recordingUrl = row.recordingUrl || row.attachmentUrl;
    const recordingName = row.recordingName || row.attachmentName || "recording.mp3";
    const transcript = transcriptPreview(row);
    const hasTranscript =
      transcript.length > 0 || (row.transcriptionUtterances?.length ?? 0) > 0;

    return {
      ...row,
      date,
      time,
      phoneNumber: row.externalNumber || row.phoneNumber || "—",
      callType: (
        <ConflictCallTypeBadge
          value={isGraceBotCall(row) ? "inbound" : row.callType}
        />
      ),
      organizationName: orgDisplayName(row),
      attachment: recordingUrl ? (
        <a
          href={recordingUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-start gap-2 text-primary hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <Play className="h-4 w-4 mt-0.5 shrink-0" />
          <span className="flex flex-col leading-tight">
            <span className="text-sm">{recordingName}</span>
            <span className="text-xs text-muted-foreground">(link)</span>
          </span>
        </a>
      ) : (
        "—"
      ),
      transcript: hasTranscript ? (
        <button
          type="button"
          className="group flex max-w-60 items-start gap-2 rounded-lg border border-grey bg-grey-light/40 px-2.5 py-2 text-left transition-colors hover:border-primary/30 hover:bg-primary/[0.04]"
          onClick={(e) => {
            e.stopPropagation();
            onOpenTranscript(row);
          }}
          title="View full transcript"
        >
          <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-medium text-[#111827] teachers group-hover:text-primary">
              View transcript
            </span>
            <span className="mt-0.5 block text-xs text-tertiary line-clamp-2 teachers">
              {transcript || "Open conversation"}
            </span>
          </span>
        </button>
      ) : (
        "—"
      ),
    };
  });
}

export default function ConflictsPage() {
  const { user } = useAuth();
  const { selectedOrgId, isAllSelected } = useOrg();
  const [activeTab, setActiveTab] = useState<ConflictsTab>("conflicts");
  const [rows, setRows] = useState<ConflictRow[]>([]);
  const [invalidRows, setInvalidRows] = useState<ConflictRow[]>([]);
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [invalidFilters, setInvalidFilters] = useState<Record<string, unknown>>({});
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last30");
  const [resolveOpen, setResolveOpen] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState<ConflictRow | null>(
    null,
  );
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcriptConflict, setTranscriptConflict] = useState<ConflictRow | null>(
    null,
  );

  const { startDate, endDate } = useMemo(
    () => getReportDateRange(datePreset),
    [datePreset],
  );

  const { apiData: organizationsData } = useGetData(
    "/organizations",
    { limit: 100 },
    false,
    user?.role === "admin" || user?.role === "manager",
  );
  const organizations = organizationsData?.data?.organizations || [];

  const tableOrgId =
    selectedOrgId && selectedOrgId !== "anthro-care" ? selectedOrgId : "";

  const filterOptions = useMemo(() => {
    const options: any[] = [];
    if (isAllSelected && organizations.length > 0) {
      options.push({
        label: "Organisation",
        key: "organizationId",
        isMulti: false,
        values: [
          { label: "All", key: "all" },
          ...organizations.map((org: any) => ({
            label: org?.organizationName || "Unnamed",
            key: org?._id,
          })),
        ],
      });
    }
    return options;
  }, [isAllSelected, organizations]);

  const tableFilters = useMemo(() => {
    const next: Record<string, unknown> = {
      ...filters,
      startDate,
      endDate,
    };
    if (next.organizationId === "all" || next.organizationId === "") {
      delete next.organizationId;
    }
    return next;
  }, [filters, startDate, endDate]);

  const invalidTableFilters = useMemo(() => {
    const next: Record<string, unknown> = { ...invalidFilters };
    if (next.organizationId === "all" || next.organizationId === "") {
      delete next.organizationId;
    }
    return next;
  }, [invalidFilters]);

  const handleSetRows = (payload: unknown) => {
    if (Array.isArray(payload)) {
      setRows(extractUnassignedInteractions(payload));
      return;
    }
    setRows(extractUnassignedInteractions(payload));
  };

  const handleSetInvalidRows = (payload: unknown) => {
    if (Array.isArray(payload)) {
      setInvalidRows(extractInvalidInteractions(payload));
      return;
    }
    setInvalidRows(extractInvalidInteractions(payload));
  };

  const dateToolbar = (
    <div className="flex items-center gap-2">
      <Select
        value={datePreset}
        onValueChange={(v) => setDatePreset(v as ReportDatePreset)}
      >
        <SelectTrigger className="h-9 w-[180px] teachers text-sm">
          <SelectValue placeholder="Date filter" />
        </SelectTrigger>
        <SelectContent>
          {DATE_PRESET_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const openResolve = (row: ConflictRow) => {
    setSelectedConflict(row);
    setResolveOpen(true);
  };

  const openTranscript = useCallback((row: ConflictRow) => {
    setTranscriptConflict(row);
    setTranscriptOpen(true);
  }, []);

  const conflictViewRows = useMemo(
    () => buildConflictViewRows(rows, openTranscript),
    [rows, openTranscript],
  );

  const invalidViewRows = useMemo(
    () => buildConflictViewRows(invalidRows, openTranscript),
    [invalidRows, openTranscript],
  );

  return (
    <DashboardLayout activePage="/conflicts">
      <div className="space-y-4">
        <ToggleGroup
          type="single"
          value={activeTab}
          onValueChange={(value) => {
            if (value) setActiveTab(value as ConflictsTab);
          }}
          className="teachers flex w-full sm:w-auto"
        >
          <ToggleGroupItem
            value="conflicts"
            aria-label="Conflict"
            style={{
              backgroundColor: activeTab === "conflicts" ? "#3463AA" : "#EEEEEE",
            }}
            className={`px-6 py-2 text-sm flex-1 md:flex-initial font-medium ${activeTab === "conflicts" ? "bg-primary-dark !important text-white" : "bg-grey-light"}`}
          >
            <Phone className="h-4 w-4" />
            Conflict
          </ToggleGroupItem>
          <ToggleGroupItem
            value="invalid"
            aria-label="Invalid Conflicts"
            style={{
              backgroundColor: activeTab === "invalid" ? "#3463AA" : "#EEEEEE",
            }}
            className={`px-6 py-2 text-sm flex-1 md:flex-initial font-medium ${activeTab === "invalid" ? "bg-primary-dark !important text-white" : "bg-grey-light"}`}
          >
            <PhoneOff className="h-4 w-4" />
            Invalid Conflicts
          </ToggleGroupItem>
        </ToggleGroup>

        {activeTab === "conflicts" ? (
          <TableWithSearch
            key={`conflicts-${tableOrgId || "all"}-${startDate}-${endDate}`}
            heading=""
            url={CONFLICTS_API_URL}
            apiDataKey=""
            universalLoadingSkeleton
            isIncludeSearch
            searchPlaceholder="Search by phone number"
            columnHeaders={conflictTableHeaders}
            data={rows}
            setData={handleSetRows}
            viewData={conflictViewRows}
            filters={tableFilters}
            setFilters={setFilters}
            filtersOptions={filterOptions}
            headerExtraControls={dateToolbar}
            params={{
              ...(tableOrgId ? { orgId: tableOrgId } : {}),
            }}
            fetchCondition={true}
            isIgnoreSearchParams
            isActionColumn={false}
            handleAction={() => {}}
            clickRow={() => {}}
            moreActions={[
              {
                label: "Resolve",
                onClick: (data: ConflictRow) => openResolve(data),
                isShow: () => true,
              },
            ]}
            isColumnSelection
            columnPreferenceKey="conflicts-list"
          />
        ) : (
          <TableWithSearch
            key={`invalid-conflicts-${tableOrgId || "all"}`}
            heading=""
            url={INVALID_CONFLICTS_API_URL}
            apiDataKey=""
            universalLoadingSkeleton
            isIncludeSearch={false}
            searchPlaceholder="Search by phone number"
            columnHeaders={conflictTableHeaders}
            data={invalidRows}
            setData={handleSetInvalidRows}
            viewData={invalidViewRows}
            filters={invalidTableFilters}
            setFilters={setInvalidFilters}
            filtersOptions={[]}
            params={{
              ...(tableOrgId ? { orgId: tableOrgId } : {}),
            }}
            fetchCondition={activeTab === "invalid"}
            isIgnoreSearchParams
            isActionColumn={false}
            handleAction={() => {}}
            clickRow={() => {}}
            moreActions={[]}
            columnPreferenceKey="invalid-conflicts-list"
          />
        )}
      </div>

      <TranscriptDialog
        open={transcriptOpen}
        onOpenChange={setTranscriptOpen}
        conflict={transcriptConflict}
        transcript={String(transcriptConflict?.transcript ?? "").trim()}
        utterances={transcriptConflict?.transcriptionUtterances ?? []}
      />

      <ResolveConflictDialog
        open={resolveOpen}
        onOpenChange={setResolveOpen}
        conflict={selectedConflict}
        onResolved={(conflictId) => {
          setRows((prev) => prev.filter((r) => r._id !== conflictId));
          setSelectedConflict(null);
        }}
      />
    </DashboardLayout>
  );
}
